import React, { useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Overlay, Popover } from 'react-bootstrap'

const TRIGGER = {
  CLICK: 'click',
  HOVER: 'hover',
}
let idNo = 0

const ToolbarButton = ({
  id = null,
  className = '',
  tooltip = null,
  tooltipOptions: {
    trigger: tooltipTrigger = TRIGGER.HOVER,
    placement: tooltipPlacement = 'right',
    activeOnDisabled: tooltipActiveOnDisabled = false,
    showTooltip: externalShowTooltipProp = null,
  } = {},
  dropdownMenu = null,
  dropdownMenuOptions: {
    placement: dropdownMenuPlacement = 'right',
  } = {},
  dropdownModal = null,
  dropdownModalOptions: {
    id: modalId = null,
    placement: dropdownModalPlacement = 'right',
  } = {},
  onClick = null,
  disabled = false,
  selected = false,
  children,
}) => {
  if (dropdownMenu && dropdownModal) {
    throw new Error('Only one of dropdownMenu or dropdownModal should be specified')
  }
  if (dropdownModal && !modalId) {
    throw new Error('Dropdown modal id not specified')
  }
  const [buttonId] = useState(id || `tbButton-${idNo++}`) // eslint-disable-line
  const [showTooltip, setShowTooltip] = useState(false)
  const [externalShowTooltip, setExternalShowTooltip] = useState(null)
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const showDropdownMenuRef = useRef({
    showDropdownMenu,
    buttonClicked: false,
  })
  const [triggerTooltip] = useState(tooltipTrigger || TRIGGER.HOVER)

  const [showDropdownModal, setShowDropdownModal] = useState(false)
  const [dropdownModalHidden, setDropdownModalHidden] = useState(false)
  const modalStatus = useSelector((state) => state.toolbar.modal[modalId])
  const [noTransition, setNoTransition] = useState(false)

  const target = useRef(null)
  const buttonRef = useRef(null)

  const toggleDropdownMenu = () => {
    const value = !showDropdownMenuRef.current.showDropdownMenu
    showDropdownMenuRef.current.showDropdownMenu = value
    showDropdownMenuRef.current.buttonClicked = value
    setShowDropdownMenu(value)
  }

  // set showTooltip from prop tooltipOptions.showTooltip
  // tooltipOptions.showTooltip - may be true|false, otherwise it's null
  // tooltipOptions.keep - truthy|falsy
  //
  // - if tooltipOptions.showTooltip is true|false the internal showTooltip value
  // is updated with tooltipOptions.showTooltip value, so this way the parent may
  // show/hide tooltip at wish
  // - if tooltipOptions.keep is truthy, tooltipOptions.showTooltip is saved as
  //   'externalShowTooltip' with it's value (null|true|false)
  // - if tooltipOptions.keep is falsy, tooltipOptions.showTooltip is saved as null
  //
  // - at render, if externalShowTooltip is null, the showTooltip value is used
  //   else the externalShowTooltip value is used to display/hide the tooltip
  // - if externalShowTooltip is true|false (was saved with keep = true) the internal
  //   behaviour (mouse handlers for tooltips) is bypassed
  useEffect(() => {
    let newShowTooltip = null
    let keepExternal = false
    // get showTooltip value from prop
    if (externalShowTooltipProp === false || externalShowTooltipProp === true) {
      newShowTooltip = externalShowTooltipProp
    } else if (externalShowTooltipProp !== null && typeof externalShowTooltipProp === 'object') {
      const { value, keep } = externalShowTooltipProp
      if (value === false || value === true) {
        newShowTooltip = value
      }
      keepExternal = keep
    }
    if (newShowTooltip === true || newShowTooltip === false) {
      setShowTooltip(newShowTooltip)
    }
    // save/reset showTooltip from parent
    setExternalShowTooltip(keepExternal ? newShowTooltip : null)
  }, [externalShowTooltipProp])

  // dropdown hide on click
  // register/deregister document click event on mount/unmount
  useEffect(() => {
    if (!dropdownMenu) {
      return
    }
    const documentOnClick = () => {
      const { showDropdownMenu, buttonClicked } = showDropdownMenuRef.current
      if (showDropdownMenu) {
        if (buttonClicked) {
          showDropdownMenuRef.current.buttonClicked = false
          return
        }
        toggleDropdownMenu()
      }
    }
    document.addEventListener('click', documentOnClick)
    // unmount cb
    return () => document.removeEventListener('click', documentOnClick)
  }, []) // [] -> called only on mount

  useEffect(() => {
    if (modalStatus === 'open') {
      setShowDropdownModal(true)
      setDropdownModalHidden(false)
      return
    }
    if (modalStatus === 'hidden') {
      setShowDropdownModal(false)
      setDropdownModalHidden(true)
      return
    }
    setShowDropdownModal(false)
    setDropdownModalHidden(false)
  }, [modalStatus])

  useEffect(() => {
    let tmr = null
    if (disabled || (dropdownModalHidden && selected)) {
      setNoTransition(true) // do not use background transitions on disabled buttons
    } else {
      // restore transition after render
      tmr = setTimeout(() => setNoTransition(false), 0)
    }
    return () => clearTimeout(tmr)
  }, [disabled])

  const handleTooltip = (on, source) => {
    if (!tooltip) {
      return
    }
    if (externalShowTooltip === false || externalShowTooltip === true) {
      // showTooltip is managed by parent
      return
    }
    if (disabled && !tooltipActiveOnDisabled) {
      // hide tootip when button become disabled
      setShowTooltip(false)
    } else if (triggerTooltip === TRIGGER.HOVER && source === TRIGGER.HOVER) {
      // button configured for hovering tooltips, from hover event
      setShowTooltip(on)
    } else if (triggerTooltip === TRIGGER.HOVER && source === TRIGGER.CLICK) {
      // button configured for hovering tooltips, from click event
      // (hide tootip when the button is clicked)
      setShowTooltip(false)
    } else if (triggerTooltip === TRIGGER.CLICK && source === TRIGGER.CLICK) {
      setShowTooltip(!showTooltip)
    }
  }

  const handleClick = (event) => {
    handleTooltip(true, TRIGGER.CLICK)
    if (dropdownMenu) {
      toggleDropdownMenu()
    }
    if (onClick && typeof onClick === 'function') {
      onClick(event)
    }
  }

  const handleMouseEnter = () => {
    handleTooltip(true, TRIGGER.HOVER)
  }

  const handleMouseLeave = () => {
    handleTooltip(false, TRIGGER.HOVER)
  }

  const classes = ['toolbarButton']
  if (dropdownMenu) {
    classes.push('dropdownMenuButton')
    if (showDropdownMenu) {
      classes.push('selectedButton')
    }
  } else if (selected) {
    classes.push('selectedButton')
  }
  if (dropdownModal) {
    classes.push('dropdownModalButton')
    if (dropdownModalHidden && selected) {
      classes.push('dropdownModalHidden')
    }
  }
  if (disabled) {
    classes.push('disabled')
  }
  if (noTransition) {
    classes.push('noTransition')
  }
  if (className) {
    classes.push(className)
  }

  return (
    <React.Fragment>
      <div
        ref={target}
        className='toolbarButtonWrapper'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className={classes.join(' ')}
          id={buttonId}
          onClick={handleClick}
          disabled={disabled}
          ref={buttonRef}
        >
          {children}
        </button>
        {disabled && tooltip && tooltipActiveOnDisabled &&
          // transparent overlay over the disabled button
          // so the browser send correctly mouseEnter, mouseLeave
          <div className={'tbButtonDisableOverlay'}></div>
        }
      </div>
      {tooltip &&
        <Overlay
          target={target.current}
          show={externalShowTooltip === null ? showTooltip : externalShowTooltip}
          placement={tooltipPlacement}
          transition={true}
        >
          <Popover id={`${buttonId}-tooltip`} bsPrefix={'popover tb-popover tb-tooltip'}>
            <Popover.Content>
              { tooltip }
            </Popover.Content>
          </Popover>
        </Overlay>
      }
      {dropdownMenu &&
        <Overlay
          target={target.current}
          show={showDropdownMenu}
          placement={dropdownMenuPlacement}
          transition={true}
        >
          <Popover id={`${buttonId}-dropdown-menu`} bsPrefix={'popover tb-popover tb-dropdown-menu'}>
            <Popover.Content>
              { dropdownMenu }
            </Popover.Content>
          </Popover>
        </Overlay>
      }
      {dropdownModal &&
        <Overlay
          target={target.current}
          show={showDropdownModal}
          placement={dropdownModalPlacement}
          transition={true}
        >
          <Popover id={`${buttonId}-dropdown-modal`} bsPrefix={'popover tb-popover tb-dropdown-modal'}>
            <Popover.Content>
              { dropdownModal }
            </Popover.Content>
          </Popover>
        </Overlay>
      }
    </React.Fragment>
  )
}


const EXTERNAL_SHOW_TOOLTIP_VALUES = [undefined, null, true, false]
ToolbarButton.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  tooltip: PropTypes.node,
  tooltipOptions: PropTypes.shape({
    placement: PropTypes.string,
    trigger: PropTypes.string,
    activeOnDisabled: PropTypes.bool,
    showTooltip: PropTypes.oneOfType([
      PropTypes.oneOf([...EXTERNAL_SHOW_TOOLTIP_VALUES]),
      PropTypes.shape({
        value: PropTypes.oneOf([...EXTERNAL_SHOW_TOOLTIP_VALUES]),
        keep: PropTypes.bool,
      })
    ]),
  }),
  dropdownMenu: PropTypes.node,
  dropdownMenuOptions: PropTypes.shape({
    placement: PropTypes.string,
    trigger: PropTypes.string,
  }),
  dropdownModal: PropTypes.node,
  dropdownModalOptions: PropTypes.shape({
    id: PropTypes.string,
    placement: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}

export default ToolbarButton
